import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

interface BeforeInstallPromptEvent extends Event {
    prompt(): Promise<void>;
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISS_STORAGE_KEY = 'pwaInstallDismissedUntil';
const DISMISS_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class PwaInstallService {
    private deferredPrompt: BeforeInstallPromptEvent | null = null;
    readonly canInstall$ = new BehaviorSubject<boolean>(false);

    constructor() {
        window.addEventListener('beforeinstallprompt', (event: Event) => {
            event.preventDefault();
            this.deferredPrompt = event as BeforeInstallPromptEvent;

            if (this.isWithinDismissCooldown()) {
                return;
            }

            this.canInstall$.next(true);
        });

        window.addEventListener('appinstalled', () => {
            this.deferredPrompt = null;
            this.canInstall$.next(false);
        });
    }

    private isWithinDismissCooldown(): boolean {
        const dismissedUntil = Number(localStorage.getItem(DISMISS_STORAGE_KEY) ?? 0);
        return Date.now() < dismissedUntil;
    }

    async promptInstall(): Promise<void> {
        if (!this.deferredPrompt) {
            return;
        }

        await this.deferredPrompt.prompt();
        await this.deferredPrompt.userChoice;
        this.deferredPrompt = null;
        this.canInstall$.next(false);
    }

    dismissForNow(): void {
        localStorage.setItem(DISMISS_STORAGE_KEY, String(Date.now() + DISMISS_COOLDOWN_MS));
        this.canInstall$.next(false);
    }
}
